import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import rehypeSanitize from "rehype-sanitize";
import { 
  Plus, 
  Search, 
  FileText, 
  Save, 
  Trash2, 
  Eye, 
  Edit3,
  AlertTriangle,
  Image as ImageIcon,
  X,
  Type,
  Hash,
  ChevronDown,
  ChevronUp,
  Info,
  Link as LinkIcon,
  Bold,
  Italic,
  List as ListIcon,
  ListOrdered,
  Quote,
  Table as TableIcon,
  Minus,
  CheckCircle,
  ExternalLink,
  MoreHorizontal,
  Send,
  EyeOff
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { SeletorDeImagem } from "@/components/admin/SeletorDeImagem";
import { SEOAnalysisPanel } from "@/components/admin/SEOAnalysisPanel";
import { LinkModal } from "@/components/admin/LinkModal";
import { SuggestedLinks } from "@/components/admin/SuggestedLinks";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Route = createFileRoute("/admin/paginas")({
  component: AdminBlogPage,
});

function AdminBlogPage() {
  const queryClient = useQueryClient();
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [seoOpen, setSeoOpen] = useState(true);
  const [newSecondaryKW, setNewSecondaryKW] = useState("");
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedTextForLink, setSelectedTextForLink] = useState("");
  const [isCheckingLinks, setIsCheckingLinks] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: posts, isLoading } = useQuery({
    queryKey: ["admin-artigos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("artigos")
        .select("*, categorias_blog(nome, slug)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: categorias } = useQuery({
    queryKey: ["admin-categorias-blog"],
    queryFn: async () => {
      const { data, error } = await supabase.from("categorias_blog").select("*");
      if (error) throw error;
      return data;
    },
  });

  const mutation = useMutation({
    mutationFn: async (post: any) => {
      // Basic validation for publishing
      if (post.status === "publicado") {
        const missingFields = [];
        if (!post.titulo?.trim()) missingFields.push("Título");
        if (!post.slug?.trim()) missingFields.push("Slug");
        if (!post.resumo?.trim()) missingFields.push("Resumo");
        if (!post.conteudo?.trim()) missingFields.push("Conteúdo");
        if (!post.categoria_id) missingFields.push("Categoria");

        if (missingFields.length > 0) {
          throw new Error(`Campos obrigatórios para publicação ausentes: ${missingFields.join(", ")}`);
        }

        // SEO check before publishing
        if ((post.seo_score || 0) < 60) {
          if (!confirm(`A análise de SEO está em ${post.seo_score || 0} pontos. Publicar mesmo assim?`)) {
            throw new Error("Publicação cancelada para melhoria do SEO.");
          }
        }
      }

      const { categorias_blog, ...postData } = post;
      
      if (postData.palavras_chave_secundarias && !Array.isArray(postData.palavras_chave_secundarias)) {
        postData.palavras_chave_secundarias = [];
      }

      // Validar links antes de salvar
      const brokenLinks = await validateInternalLinks(post.conteudo);
      if (brokenLinks.length > 0) {
        toast.warning(`Atenção: Links internos problemáticos detectados: ${brokenLinks.join(", ")}`, {
          duration: 8000,
        });
      }

      if (post.id) {
        const { data, error } = await supabase
          .from("artigos")
          .update(postData)
          .eq("id", post.id)
          .select();
        
        if (error) throw error;
        if (!data || data.length === 0) throw new Error("Nenhuma linha foi atualizada. Verifique suas permissões.");
        return data[0];
      } else {
        const { data, error } = await supabase
          .from("artigos")
          .insert([postData])
          .select();
          
        if (error) throw error;
        if (!data || data.length === 0) throw new Error("Erro ao criar artigo.");
        return data[0];
      }
    },
    onSuccess: (data: any, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ["admin-artigos"] });
      
      if (data?.status === "publicado") {
        toast.success("Artigo publicado!", {
          action: {
            label: "Ver artigo",
            onClick: () => window.open(`/guias/${data.slug}`, '_blank')
          }
        });
      } else {
        toast.success("Rascunho salvo com sucesso!");
      }
      
      // Se a mutation veio do editor (isEditing=true), fecha o editor.
      // Se veio da lista via menu de ações, o estado isEditing já está false.
      setIsEditing(false);
      setSelectedPost(null);
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao salvar artigo");
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("artigos").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-artigos"] });
      toast.success("Artigo excluído!");
    }
  });

  const filteredPosts = posts?.filter(post => 
    post.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const insertImageAtCursor = (midia: any) => {
    if (!textareaRef.current) return;
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const text = selectedPost.conteudo;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const imageMarkdown = `![${midia.alt || midia.nome}](${midia.arquivo_url})`;
    const newValue = before + imageMarkdown + after;
    setSelectedPost({ ...selectedPost, conteudo: newValue });
    
    // Set focus back and move cursor
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const cursorPosition = start + imageMarkdown.length;
        textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };

  const wrapSelection = (prefix: string, suffix: string = prefix, placeholder: string = "") => {
    if (!textareaRef.current) return;
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const text = selectedPost.conteudo;
    const selection = text.substring(start, end) || placeholder;
    const before = text.substring(0, start);
    const after = text.substring(end);
    
    const newValue = before + prefix + selection + suffix + after;
    setSelectedPost({ ...selectedPost, conteudo: newValue });

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(start + prefix.length, start + prefix.length + selection.length);
      }
    }, 0);
  };

  const insertLink = (link: { text: string; url: string; targetBlank?: boolean; nofollow?: boolean }) => {
    if (!textareaRef.current) return;
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const text = selectedPost.conteudo;
    const before = text.substring(0, start);
    const after = text.substring(end);

    let linkMarkdown = `[${link.text}](${link.url})`;
    if (link.nofollow) {
      linkMarkdown = `<a href="${link.url}" rel="nofollow"${link.targetBlank ? ' target="_blank"' : ''}>${link.text}</a>`;
    }

    const newValue = before + linkMarkdown + after;
    setSelectedPost({ ...selectedPost, conteudo: newValue });
    
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newPos = start + linkMarkdown.length;
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleSuggestLink = (suggestion: { text: string; url: string }) => {
    if (!textareaRef.current) return;
    const text = selectedPost.conteudo;
    const kw = suggestion.text;
    const regex = new RegExp(kw, "i");
    const match = text.match(regex);
    
    if (match && match.index !== undefined) {
      const before = text.substring(0, match.index);
      const selection = text.substring(match.index, match.index + kw.length);
      const after = text.substring(match.index + kw.length);
      const linkMarkdown = `[${selection}](${suggestion.url})`;
      const newValue = before + linkMarkdown + after;
      setSelectedPost({ ...selectedPost, conteudo: newValue });
      toast.success("Link inserido com sucesso!");
    } else {
      insertLink(suggestion);
    }
  };

  const validateInternalLinks = async (content: string) => {
    const internalLinkRegex = /\[.*?\]\((\/guias\/.*?)\)/g;
    const matches = Array.from(content.matchAll(internalLinkRegex));
    const brokenLinks: string[] = [];

    for (const match of matches) {
      const url = match[1];
      if (!url) continue;

      const parts = url.split("/");
      const slug = parts[parts.length - 1];

      if (slug) {
        const { data, error } = await supabase
          .from("artigos")
          .select("status")
          .eq("slug", slug)
          .single();

        if (error || !data || data.status === "rascunho") {
          brokenLinks.push(url);
        }
      }
    }
    return brokenLinks;
  };

  const checkAllLinks = async () => {
    setIsCheckingLinks(true);
    toast.info("Iniciando verificação de links em todos os artigos...");
    
    try {
      const { data: allPosts, error } = await supabase.from("artigos").select("id, titulo, conteudo");
      if (error) throw error;

      let brokenCount = 0;
      for (const post of allPosts) {
        const broken = await validateInternalLinks(post.conteudo || "");
        if (broken.length > 0) {
          brokenCount++;
          console.warn(`Links quebrados em "${post.titulo}":`, broken);
        }
      }

      if (brokenCount > 0) {
        toast.warning(`Verificação concluída. Encontrados ${brokenCount} artigos com links internos quebrados ou para rascunhos. Verifique o console para detalhes.`);
      } else {
        toast.success("Verificação concluída. Todos os links internos estão saudáveis!");
      }
    } catch (err: any) {
      toast.error("Erro na verificação: " + err.message);
    } finally {
      setIsCheckingLinks(false);
    }
  };

  const insertTable = () => {
    const tableTemplate = "\n| Coluna 1 | Coluna 2 | Coluna 3 |\n| :--- | :--- | :--- |\n| Dado 1 | Dado 2 | Dado 3 |\n| Dado 4 | Dado 5 | Dado 6 |\n";
    wrapSelection("", "", tableTemplate);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0C1A12]">Páginas do Blog</h1>
          <p className="text-gray-500">Gerencie guias e artigos da plataforma.</p>
        </div>
        <Button 
          onClick={() => {
            setSelectedPost({
              titulo: "",
              slug: "",
              conteudo: "",
              resumo: "",
              status: "rascunho",
              categoria_id: categorias?.[0]?.id,
              imagem_capa: null
            });
            setIsEditing(true);
          }}
          className="bg-primary text-[#0C1A12] font-bold"
        >
          <Plus className="w-4 h-4 mr-2" /> Novo Artigo
        </Button>
        <Button 
          variant="outline" 
          onClick={checkAllLinks}
          disabled={isCheckingLinks}
          className="ml-2 border-primary/20 text-gray-600"
        >
          <LinkIcon className="w-4 h-4 mr-2" /> 
          {isCheckingLinks ? "Verificando..." : "Verificar Links Internos"}
        </Button>
      </div>

      {!isEditing ? (
        <Card>
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Buscar artigos..." 
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {filteredPosts?.map((post) => {
                const isCannibalized = filteredPosts.some(p => 
                  p.id !== post.id && 
                  p.palavra_chave && 
                  post.palavra_chave && 
                  p.palavra_chave.toLowerCase().trim() === post.palavra_chave.toLowerCase().trim()
                );

                return (
                  <div key={post.id} className="py-4 flex items-center justify-between group">
                    <div className="flex items-center gap-4">
                      {post.imagem_capa ? (
                        <img 
                          src={post.imagem_capa} 
                          className="w-10 h-10 object-cover rounded-lg"
                          alt={post.titulo}
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                          <FileText className="w-5 h-5" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-[#0C1A12]">{post.titulo}</h3>
                          {isCannibalized && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  2 artigos disputam este termo
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <Badge 
                            variant="outline" 
                            className={cn(
                              "text-[10px] uppercase",
                              post.status === "publicado" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-700 border-gray-200"
                            )}
                          >
                            {post.status === "publicado" ? "Publicado" : "Rascunho"}
                          </Badge>
                          {post.palavra_chave && (
                            <Badge variant="secondary" className="text-[10px] bg-gray-100 text-gray-600 border-none">
                              {post.palavra_chave}
                            </Badge>
                          )}
                          {post.seo_score !== null && (
                            <Badge className={cn(
                              "text-[10px] border-none",
                              post.seo_score >= 80 ? "bg-green-100 text-green-700" : 
                              post.seo_score >= 60 ? "bg-amber-100 text-amber-700" : 
                              "bg-red-100 text-red-700"
                            )}>
                              SEO: {post.seo_score}
                            </Badge>
                          )}
                          <span>•</span>
                          <span>{post.categorias_blog?.nome}</span>
                          <span>•</span>
                          <span>{post.created_at ? format(new Date(post.created_at), "dd MMM, yyyy", { locale: ptBR }) : ''}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedPost({
                              ...post,
                              palavras_chave_secundarias: post.palavras_chave_secundarias || []
                            });
                            setIsEditing(true);
                          }}>
                            <Edit3 className="w-4 h-4 mr-2" /> Editar
                          </DropdownMenuItem>
                          
                          {post.status === "rascunho" ? (
                            <DropdownMenuItem 
                              className="text-green-600 focus:text-green-700"
                              onClick={() => {
                                const { categorias_blog, ...postData } = post;
                                mutation.mutate({ ...postData, status: "publicado", publicado_em: new Date().toISOString() });
                              }}
                            >
                              <Send className="w-4 h-4 mr-2" /> Publicar
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              className="text-amber-600 focus:text-amber-700"
                              onClick={() => {
                                if (confirm("Deseja despublicar este artigo? Ele voltará para rascunho.")) {
                                  const { categorias_blog, ...postData } = post;
                                  mutation.mutate({ ...postData, status: "rascunho" });
                                }
                              }}
                            >
                              <EyeOff className="w-4 h-4 mr-2" /> Despublicar
                            </DropdownMenuItem>
                          )}
                          
                          <DropdownMenuItem 
                            className="text-red-600 focus:text-red-700"
                            onClick={() => {
                              if (confirm("Excluir este artigo?")) {
                                deletePostMutation.mutate(post.id);
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
              {filteredPosts?.length === 0 && (
                <div className="py-12 text-center text-gray-500">
                  Nenhum artigo encontrado.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="h-fit">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-3">
                Editor
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-[10px] uppercase",
                    selectedPost?.status === "publicado" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-700 border-gray-200"
                  )}
                >
                  {selectedPost?.status === "publicado" ? "Publicado" : "Rascunho"}
                </Badge>
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>Cancelar</Button>
                
                {selectedPost?.status === "rascunho" ? (
                  <>
                    <Button 
                      variant="outline"
                      size="sm" 
                      onClick={() => mutation.mutate({ ...selectedPost, status: "rascunho" })}
                      disabled={mutation.isPending}
                    >
                      <Save className="w-4 h-4 mr-2" /> Salvar Rascunho
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-primary text-[#0C1A12] font-bold"
                      onClick={() => mutation.mutate({ 
                        ...selectedPost, 
                        status: "publicado", 
                        publicado_em: selectedPost.publicado_em || new Date().toISOString() 
                      })}
                      disabled={mutation.isPending}
                    >
                      <Send className="w-4 h-4 mr-2" /> Publicar
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline"
                      size="sm" 
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                      onClick={() => {
                        if (confirm("Deseja despublicar este artigo?")) {
                          mutation.mutate({ ...selectedPost, status: "rascunho" });
                        }
                      }}
                      disabled={mutation.isPending}
                    >
                      <EyeOff className="w-4 h-4 mr-2" /> Despublicar
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-primary text-[#0C1A12] font-bold"
                      onClick={() => mutation.mutate({ ...selectedPost, status: "publicado" })}
                      disabled={mutation.isPending}
                    >
                      <Save className="w-4 h-4 mr-2" /> Salvar Alterações
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Imagem de Capa (16:9)</label>
                <div className="relative aspect-video w-full rounded-2xl border-2 border-dashed bg-gray-50 overflow-hidden group">
                  {selectedPost?.imagem_capa ? (
                    <>
                      <img 
                        src={selectedPost.imagem_capa} 
                        className="w-full h-full object-cover" 
                        alt="Capa"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <SeletorDeImagem 
                          onSelect={(m) => setSelectedPost({...selectedPost, imagem_capa: m.arquivo_url})}
                          trigger={<Button size="sm" variant="secondary"><ImageIcon className="w-4 h-4 mr-2" /> Trocar</Button>}
                        />
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => setSelectedPost({...selectedPost, imagem_capa: null})}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                      <ImageIcon className="w-8 h-8 text-gray-300" />
                      <SeletorDeImagem 
                        onSelect={(m) => setSelectedPost({...selectedPost, imagem_capa: m.arquivo_url})}
                        trigger={<Button variant="outline" size="sm">Selecionar Capa</Button>}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Título</label>
                <Input 
                  value={selectedPost?.titulo} 
                  onChange={(e) => setSelectedPost({...selectedPost, titulo: e.target.value})}
                  placeholder="Ex: Como comprar no Brás"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Slug</label>
                  <Input 
                    value={selectedPost?.slug} 
                    onChange={(e) => setSelectedPost({...selectedPost, slug: e.target.value})}
                    placeholder="como-comprar-no-bras"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Categoria</label>
                  <select 
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={selectedPost?.categoria_id}
                    onChange={(e) => setSelectedPost({...selectedPost, categoria_id: e.target.value})}
                  >
                    {categorias?.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium">Resumo</label>
                  <span className="text-[10px] text-gray-400">{selectedPost?.resumo?.length || 0} caracteres</span>
                </div>
                <Textarea 
                  value={selectedPost?.resumo} 
                  onChange={(e) => setSelectedPost({...selectedPost, resumo: e.target.value})}
                  placeholder="Breve descrição do artigo..."
                />
              </div>

              {/* Seção SEO */}
              <Collapsible
                open={seoOpen}
                onOpenChange={setSeoOpen}
                className="border rounded-xl p-4 bg-gray-50/50 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="p-0 h-auto hover:bg-transparent flex items-center gap-2">
                      <Hash className="w-4 h-4 text-primary" />
                      <span className="font-bold text-[#0C1A12]">SEO e Palavras-chave</span>
                      {seoOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </CollapsibleTrigger>
                </div>

                <CollapsibleContent className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-gray-700">Palavra-chave principal</label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger><Info className="w-3 h-3 text-gray-400" /></TooltipTrigger>
                          <TooltipContent>O termo exato que você quer ranquear. Use apenas um.</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input 
                      value={selectedPost?.palavra_chave || ""} 
                      onChange={(e) => setSelectedPost({...selectedPost, palavra_chave: e.target.value})}
                      placeholder="Ex: fornecedores de roupas"
                      className="bg-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-gray-700">Palavras-chave secundárias</label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger><Info className="w-3 h-3 text-gray-400" /></TooltipTrigger>
                          <TooltipContent>Variações e termos relacionados. Máximo 8.</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="flex flex-wrap gap-2 p-2 bg-white border rounded-md min-h-[40px]">
                      {selectedPost?.palavras_chave_secundarias?.map((kw: string, idx: number) => (
                        <Badge key={idx} variant="secondary" className="gap-1 bg-gray-100 text-gray-700">
                          {kw}
                          <X 
                            className="w-3 h-3 cursor-pointer hover:text-red-500" 
                            onClick={() => {
                              const newKws = [...selectedPost.palavras_chave_secundarias];
                              newKws.splice(idx, 1);
                              setSelectedPost({...selectedPost, palavras_chave_secundarias: newKws});
                            }}
                          />
                        </Badge>
                      ))}
                      <input 
                        type="text"
                        className="flex-1 outline-none text-sm min-w-[120px]"
                        placeholder={selectedPost?.palavras_chave_secundarias?.length >= 8 ? "Limite atingido" : "Adicionar... (Enter)"}
                        disabled={selectedPost?.palavras_chave_secundarias?.length >= 8}
                        value={newSecondaryKW}
                        onChange={(e) => setNewSecondaryKW(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && newSecondaryKW.trim()) {
                            e.preventDefault();
                            if (selectedPost.palavras_chave_secundarias?.length >= 8) return;
                            const newKws = [...(selectedPost.palavras_chave_secundarias || []), newSecondaryKW.trim()];
                            setSelectedPost({...selectedPost, palavras_chave_secundarias: newKws});
                            setNewSecondaryKW("");
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-700">Meta Title</label>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "text-[10px] font-bold",
                            (selectedPost?.meta_title?.length || 0) > 65 ? "text-red-500" :
                            (selectedPost?.meta_title?.length || 0) > 60 ? "text-amber-500" :
                            "text-green-600"
                          )}>
                            {selectedPost?.meta_title?.length || 0}/60
                          </span>
                          <Button 
                            variant="link" 
                            className="p-0 h-auto text-[10px]" 
                            onClick={() => setSelectedPost({...selectedPost, meta_title: selectedPost.titulo})}
                          >
                            Usar o título
                          </Button>
                        </div>
                      </div>
                      <Input 
                        value={selectedPost?.meta_title || ""} 
                        onChange={(e) => setSelectedPost({...selectedPost, meta_title: e.target.value})}
                        placeholder="Título que aparece no Google"
                        className="bg-white"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-700">Meta Description</label>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "text-[10px] font-bold",
                            (selectedPost?.meta_description?.length || 0) > 170 ? "text-red-500" :
                            (selectedPost?.meta_description?.length || 0) > 160 ? "text-amber-500" :
                            "text-green-600"
                          )}>
                            {selectedPost?.meta_description?.length || 0}/160
                          </span>
                          <Button 
                            variant="link" 
                            className="p-0 h-auto text-[10px]" 
                            onClick={() => setSelectedPost({...selectedPost, meta_description: selectedPost.resumo})}
                          >
                            Usar o resumo
                          </Button>
                        </div>
                      </div>
                      <Textarea 
                        value={selectedPost?.meta_description || ""} 
                        onChange={(e) => setSelectedPost({...selectedPost, meta_description: e.target.value})}
                        placeholder="Descrição curta para o Google"
                        className="bg-white min-h-[80px]"
                      />
                    </div>
                  </div>

                  <SEOAnalysisPanel 
                    content={selectedPost?.conteudo || ""}
                    palavraChave={selectedPost?.palavra_chave || ""}
                    palavrasSecundarias={selectedPost?.palavras_chave_secundarias || []}
                    metaTitle={selectedPost?.meta_title || ""}
                    metaDescription={selectedPost?.meta_description || ""}
                    slug={selectedPost?.slug || ""}
                    imagemCapa={selectedPost?.imagem_capa}
                    faq={selectedPost?.faq}
                    onScoreChange={(score) => {
                      if (selectedPost.seo_score !== score) {
                        setSelectedPost((prev: any) => ({ ...prev, seo_score: score }));
                      }
                    }}
                  />
                  <SuggestedLinks 
                    content={selectedPost?.conteudo || ""}
                    onInsert={handleSuggestLink}
                    currentArticleId={selectedPost?.id}
                  />
                </CollapsibleContent>
              </Collapsible>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium">Conteúdo (Markdown)</label>
                  <div className="flex items-center flex-wrap gap-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => wrapSelection("**", "**", "negrito")}>
                            <Bold className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Negrito (Ctrl+B)</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => wrapSelection("_", "_", "itálico")}>
                            <Italic className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Itálico (Ctrl+I)</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => wrapSelection("## ", "", "Título H2")}>
                            <Type className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Título H2</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => wrapSelection("### ", "", "Título H3")}>
                            <Hash className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Título H3</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => {
                            const selection = textareaRef.current ? textareaRef.current.value.substring(textareaRef.current.selectionStart, textareaRef.current.selectionEnd) : "";
                            setSelectedTextForLink(selection);
                            setIsLinkModalOpen(true);
                          }}>
                            <LinkIcon className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Inserir Link (Ctrl+K)</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => wrapSelection("- ", "", "Item")}>
                            <ListIcon className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Lista</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => wrapSelection("1. ", "", "Item")}>
                            <ListOrdered className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Lista Numerada</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => wrapSelection("> ", "", "Citação")}>
                            <Quote className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Citação</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={insertTable}>
                            <TableIcon className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Inserir Tabela</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => wrapSelection("\n---\n", "")}>
                            <Minus className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Linha Horizontal</TooltipContent>
                      </Tooltip>

                      <SeletorDeImagem 
                        onSelect={insertImageAtCursor}
                        trigger={
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                                <ImageIcon className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Inserir Imagem da Biblioteca</TooltipContent>
                          </Tooltip>
                        }
                      />
                    </TooltipProvider>
                  </div>
                </div>
                <Textarea 
                  ref={textareaRef}
                  value={selectedPost?.conteudo} 
                  onChange={(e) => setSelectedPost({...selectedPost, conteudo: e.target.value})}
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === "b") {
                      e.preventDefault();
                      wrapSelection("**", "**", "negrito");
                    }
                    if ((e.ctrlKey || e.metaKey) && e.key === "i") {
                      e.preventDefault();
                      wrapSelection("_", "_", "itálico");
                    }
                    if ((e.ctrlKey || e.metaKey) && e.key === "k") {
                      e.preventDefault();
                      const selection = textareaRef.current ? textareaRef.current.value.substring(textareaRef.current.selectionStart, textareaRef.current.selectionEnd) : "";
                      setSelectedTextForLink(selection);
                      setIsLinkModalOpen(true);
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData("text/plain");
                    const start = e.currentTarget.selectionStart;
                    const end = e.currentTarget.selectionEnd;
                    const newValue = selectedPost.conteudo.substring(0, start) + text + selectedPost.conteudo.substring(end);
                    setSelectedPost({ ...selectedPost, conteudo: newValue });
                  }}
                  className="min-h-[400px] font-mono text-sm"
                  placeholder="## Título Secundário\n\nConteúdo aqui..."
                />
              </div>
            </CardContent>
          </Card>

          <Card className="h-fit sticky top-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" /> Pré-visualização
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none border rounded-xl p-6 bg-white min-h-[600px]">
                {selectedPost?.imagem_capa && (
                  <img 
                    src={selectedPost.imagem_capa} 
                    className="w-full aspect-video object-cover rounded-xl mb-6 shadow-sm" 
                    alt="Capa" 
                  />
                )}
                {selectedPost?.titulo && (
                  <h1 className="text-3xl font-bold mb-4">{selectedPost.titulo}</h1>
                )}
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSlug, rehypeSanitize]}
                  components={{
                    h1: () => null,
                    h2: ({ ...props }) => <h2 className="text-2xl font-bold mt-8 mb-4 border-b pb-2" {...props} />,
                    a: ({ href, children, ...props }) => (
                      <a 
                        href={href} 
                        className="text-[#0C1A12] underline decoration-1 underline-offset-4 hover:decoration-[#C6F764] hover:decoration-2 transition-all inline-flex items-center"
                        {...props}
                      >
                        {children}
                        {href?.startsWith('http') && <ExternalLink className="w-3 h-3 ml-1 text-gray-400" />}
                      </a>
                    ),
                    img: ({ src, alt, title }) => (
                      <div className="my-6">
                        <img 
                          src={src} 
                          alt={alt} 
                          title={title} 
                          className="w-full rounded-xl shadow-sm"
                          loading="lazy"
                        />
                        {title && <p className="text-center text-xs text-gray-500 mt-2">{title}</p>}
                      </div>
                    ),
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-6">
                        <table className="min-w-full divide-y divide-gray-200">{children}</table>
                      </div>
                    )
                  }}
                >
                  {selectedPost?.conteudo}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <LinkModal 
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        initialText={selectedTextForLink}
        onInsert={insertLink}
      />
    </div>
  );
}
